import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { FileText, RefreshCw, Edit, CreditCard, Building2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import BackButton from '../components/BackButton';
import AddButton from '../components/AddButton';
import {
  getAccountById,
  getAccountsByType,
  getAccountPayments,
  getAccountUsage,
  updateAccount,
} from '../services/firestoreService';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import PortalBackground from '../components/PortalBackground';

function formatDateForInput(value) {
  if (!value) return '';
  const date = value?.toDate?.() || (value instanceof Date ? value : new Date(value));
  if (Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function buildDedupedPayments(stripePayments, billingPayments) {
  const fromBilling = (billingPayments || []).filter(
    (p) => !p.stripeInvoiceId && !stripePayments.some((sp) => sp.id === p.id)
  );
  const combined = [...stripePayments, ...fromBilling];
  const seenStripeIds = new Set();
  return combined.filter((p) => {
    const sid = p.stripeInvoiceId || null;
    if (sid) {
      if (seenStripeIds.has(sid)) return false;
      seenStripeIds.add(sid);
    }
    return true;
  });
}

function paymentDisplayStatus(status) {
  if (status === 'paid') return 'Paid';
  if (status === 'failed') return 'Failed';
  if (status === 'refunded') return 'Refunded';
  if (status === 'processing') return 'Processing';
  if (status === 'overdue') return 'Failed';
  return 'Processing';
}

function clientLabelFromAccount(c) {
  return c.company || [c.contact?.firstName, c.contact?.lastName].filter(Boolean).join(' ') || c.id;
}

function getPaymentSortDate(p) {
  const raw = p.createdAt?.toDate?.() || p.createdAt || p.dueDate;
  if (!raw) return null;
  const d = raw instanceof Date ? raw : new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

function paymentInDateRange(p, dateFrom, dateTo) {
  const d = getPaymentSortDate(p);
  if (!dateFrom && !dateTo) return true;
  if (!d) return !dateFrom && !dateTo;
  if (dateFrom) {
    const start = new Date(`${dateFrom}T00:00:00`);
    if (d < start) return false;
  }
  if (dateTo) {
    const end = new Date(`${dateTo}T23:59:59.999`);
    if (d > end) return false;
  }
  return true;
}

const TRANSACTIONS_PAGE_SIZE = 10;

/** MRR / active count: treat Stripe-backed subs as active unless clearly ended */
function subscriptionIsActive(acc) {
  if (!acc) return false;
  const s = (acc.billing?.status || '').toLowerCase();
  if (['canceled', 'cancelled', 'incomplete_expired'].includes(s)) return false;
  if (['active', 'trialing', 'past_due'].includes(s)) return true;
  if (acc.billing?.stripeSubscriptionId) return !['canceled', 'cancelled', 'unpaid'].includes(s);
  return false;
}

const ClientBilling = () => {
  const { darkMode } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const clientId = searchParams.get('clientId') || '';

  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [aggregatedPayments, setAggregatedPayments] = useState(null);
  const [allPaymentsLoading, setAllPaymentsLoading] = useState(false);
  const [account, setAccount] = useState(null);
  const [accountLoading, setAccountLoading] = useState(false);
  const [stripePayments, setStripePayments] = useState([]);
  const [stripeUsage, setStripeUsage] = useState(null);
  const [syncBillingLoading, setSyncBillingLoading] = useState(false);

  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [newPayment, setNewPayment] = useState({
    amount: '',
    dueDate: '',
    status: 'processing',
  });

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [transactionsPage, setTransactionsPage] = useState(1);

  const isDevelopment = process.env.NODE_ENV === 'development';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setClientsLoading(true);
        const list = await getAccountsByType('Clients');
        if (!cancelled) {
          setClients(
            [...list].sort((a, b) =>
              (a.company || '').localeCompare(b.company || '', undefined, { sensitivity: 'base' })
            )
          );
        }
      } catch (e) {
        console.error('ClientBilling: load clients', e);
        if (!cancelled) setClients([]);
      } finally {
        if (!cancelled) setClientsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadAccountAndPayments = useCallback(async (id) => {
    if (!id) {
      setAccount(null);
      setStripePayments([]);
      setStripeUsage(null);
      return;
    }
    setAccountLoading(true);
    try {
      const data = await getAccountById(id);
      setAccount(data);
      const payments = await getAccountPayments(id);
      setStripePayments(payments);
      const billing = data?.billing;
      const periodStart = billing?.currentPeriodStart;
      if (periodStart) {
        const periodKey = new Date(periodStart).toISOString().split('T')[0];
        const usage = await getAccountUsage(id, periodKey);
        setStripeUsage(usage);
      } else {
        setStripeUsage(null);
      }
    } catch (e) {
      console.error('ClientBilling: load account', e);
      setAccount(null);
      setStripePayments([]);
      setStripeUsage(null);
    } finally {
      setAccountLoading(false);
    }
  }, []);

  useEffect(() => {
    if (clientId) {
      loadAccountAndPayments(clientId);
      setAggregatedPayments(null);
    } else {
      loadAccountAndPayments('');
    }
  }, [clientId, loadAccountAndPayments]);

  useEffect(() => {
    if (clientId) return;
    let cancelled = false;
    (async () => {
      if (clientsLoading) return;
      if (!clients.length) {
        setAggregatedPayments([]);
        return;
      }
      setAllPaymentsLoading(true);
      try {
        const parts = await Promise.all(
          clients.map(async (c) => {
            try {
              const [stripe, acc] = await Promise.all([
                getAccountPayments(c.id),
                getAccountById(c.id),
              ]);
              const name = clientLabelFromAccount(c);
              const merged = buildDedupedPayments(stripe, acc?.billing?.payments);
              return merged.map((p) => ({
                ...p,
                _clientId: c.id,
                _clientName: name,
              }));
            } catch (e) {
              console.error('ClientBilling: payments for', c.id, e);
              return [];
            }
          })
        );
        const flat = parts.flat();
        flat.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || a.createdAt || a.dueDate;
          const dateB = b.createdAt?.toDate?.() || b.createdAt || b.dueDate;
          return new Date(dateB || 0) - new Date(dateA || 0);
        });
        if (!cancelled) setAggregatedPayments(flat);
      } finally {
        if (!cancelled) setAllPaymentsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId, clients, clientsLoading]);

  const dedupedPayments = useMemo(
    () =>
      buildDedupedPayments(stripePayments, account?.billing?.payments).sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || a.createdAt || a.dueDate;
        const dateB = b.createdAt?.toDate?.() || b.createdAt || b.dueDate;
        return new Date(dateB || 0) - new Date(dateA || 0);
      }),
    [stripePayments, account?.billing?.payments]
  );

  const lifetimePaidDisplayAmount = useMemo(() => {
    const sumPaid = dedupedPayments
      .filter((p) => {
        const s = (p.status || '').toString().toLowerCase();
        return s === 'paid' || s === 'succeeded';
      })
      .reduce((sum, p) => sum + Math.abs(Number(p.amount) || 0), 0);
    const stored = account?.billing?.lifetimePaid ?? account?.billing?.lifetimeAmount ?? 0;
    if (dedupedPayments.length > 0) return sumPaid;
    return stored;
  }, [account?.billing?.lifetimePaid, account?.billing?.lifetimeAmount, dedupedPayments]);

  const filteredPayments = useMemo(() => {
    const list = clientId ? dedupedPayments : aggregatedPayments || [];
    return list.filter((p) => {
      if (!paymentInDateRange(p, dateFrom, dateTo)) return false;
      if (statusFilter !== 'all' && paymentDisplayStatus(p.status) !== statusFilter) return false;
      return true;
    });
  }, [clientId, dedupedPayments, aggregatedPayments, dateFrom, dateTo, statusFilter]);

  useEffect(() => {
    setTransactionsPage(1);
  }, [clientId, dateFrom, dateTo, statusFilter]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredPayments.length / TRANSACTIONS_PAGE_SIZE));
    setTransactionsPage((p) => (p > maxPage ? maxPage : p));
  }, [filteredPayments.length]);

  const transactionsTotalPages = Math.max(
    1,
    Math.ceil(filteredPayments.length / TRANSACTIONS_PAGE_SIZE)
  );
  const transactionsPageSafe = Math.min(transactionsPage, transactionsTotalPages);
  const paginatedTransactions = useMemo(() => {
    const start = (transactionsPageSafe - 1) * TRANSACTIONS_PAGE_SIZE;
    return filteredPayments.slice(start, start + TRANSACTIONS_PAGE_SIZE);
  }, [filteredPayments, transactionsPageSafe]);

  const billingMetrics = useMemo(() => {
    const paidRevenue = filteredPayments
      .filter((p) => paymentDisplayStatus(p.status) === 'Paid')
      .reduce((sum, p) => sum + Math.abs(p.amount || 0), 0);

    if (clientId && account) {
      const mrr = subscriptionIsActive(account)
        ? (account.program?.monthlyAmount ?? account.billing?.monthlyAmount ?? 0)
        : 0;
      return {
        totalRevenue: paidRevenue,
        mrr,
        activeSubscriptions: subscriptionIsActive(account) ? 1 : 0,
      };
    }

    if (!clientId && clients.length) {
      const activeClients = clients.filter(subscriptionIsActive);
      const mrr = activeClients.reduce(
        (sum, c) => sum + (c.program?.monthlyAmount ?? c.billing?.monthlyAmount ?? 0),
        0
      );
      return {
        totalRevenue: paidRevenue,
        mrr,
        activeSubscriptions: activeClients.length,
      };
    }

    return { totalRevenue: paidRevenue, mrr: 0, activeSubscriptions: 0 };
  }, [clientId, account, clients, filteredPayments]);

  const handleClientChange = (e) => {
    const id = e.target.value;
    if (id) setSearchParams({ clientId: id });
    else setSearchParams({});
  };

  const handleSyncStripeBilling = async () => {
    if (!clientId) return;
    try {
      setSyncBillingLoading(true);
      const syncStripeBillingFn = httpsCallable(functions, 'syncStripeBilling');
      const { data } = await syncStripeBillingFn({ accountId: clientId });
      const acc = await getAccountById(clientId);
      setAccount(acc);
      const payments = await getAccountPayments(clientId);
      setStripePayments(payments);
      if (data?.subscriptionSyncFailed) {
        alert(
          `Lifetime amount saved from payment records. Subscription details could not be loaded: ${data.subscriptionError || 'unknown error'}.`
        );
      } else if (data?.lifetimeOnly) {
        alert('Lifetime amount updated from payment records.');
      } else {
        alert('Billing synced from Stripe. Lifetime amount, customer ID, and billing period are updated.');
      }
    } catch (error) {
      console.error('Sync Stripe billing:', error);
      alert(error.message || 'Failed to sync billing from Stripe');
    } finally {
      setSyncBillingLoading(false);
    }
  };

  const handleEditPayment = (payment) => {
    setNewPayment({
      amount: payment.amount,
      dueDate: formatDateForInput(payment.dueDate || payment.createdAt),
      status: payment.status === 'overdue' ? 'failed' : payment.status === 'pending' ? 'processing' : payment.status,
    });
    setEditingPaymentId(payment.id);
    setShowAddPaymentModal(true);
  };

  const handleUpdatePayment = async (paymentId, updatedPayment) => {
    const updatedPayments =
      account.billing?.payments?.map((p) => (p.id === paymentId ? { ...p, ...updatedPayment } : p)) || [];
    const updatedAccount = {
      ...account,
      billing: {
        ...account.billing,
        payments: updatedPayments,
      },
    };
    setAccount(updatedAccount);
    // Only the payments list — writing the page's whole (possibly stale) account would revert
    // Stripe syncs and Billing-tab edits made since it loaded.
    await updateAccount(clientId, { 'billing.payments': updatedPayments });
  };

  const handleDeletePayment = async (paymentId) => {
    const updatedPayments = account.billing?.payments?.filter((p) => p.id !== paymentId) || [];
    const updatedAccount = {
      ...account,
      billing: {
        ...account.billing,
        payments: updatedPayments,
      },
    };
    setAccount(updatedAccount);
    await updateAccount(clientId, { 'billing.payments': updatedPayments });
  };

  const txRangeStart =
    filteredPayments.length === 0 ? 0 : (transactionsPageSafe - 1) * TRANSACTIONS_PAGE_SIZE + 1;
  const txRangeEnd = Math.min(
    transactionsPageSafe * TRANSACTIONS_PAGE_SIZE,
    filteredPayments.length
  );

  const transactionsPaginationBar =
    filteredPayments.length > TRANSACTIONS_PAGE_SIZE ? (
      <div
        className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4 text-sm ${
          darkMode ? 'text-gray-300' : 'text-gray-600'
        }`}
      >
        <p>
          Showing {txRangeStart}–{txRangeEnd} of {filteredPayments.length}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={transactionsPageSafe <= 1}
            onClick={() => setTransactionsPage((p) => Math.max(1, p - 1))}
            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed ${
              darkMode
                ? 'border-gray-600 text-gray-200 hover:bg-gray-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>
          <span className="px-2 tabular-nums text-gray-500 dark:text-gray-400">
            Page {transactionsPageSafe} of {transactionsTotalPages}
          </span>
          <button
            type="button"
            disabled={transactionsPageSafe >= transactionsTotalPages}
            onClick={() => setTransactionsPage((p) => Math.min(transactionsTotalPages, p + 1))}
            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed ${
              darkMode
                ? 'border-gray-600 text-gray-200 hover:bg-gray-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    ) : null;

  return (
    <div className="min-h-screen relative">
      <PortalBackground />
      <div className="relative w-full max-w-6xl mx-auto">
        <div className="relative mb-8">
          <div className="absolute left-0 top-1">
            <BackButton to="/crm/leads" />
          </div>
          <div className="text-center px-14 sm:px-16">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
              Client billing
            </h1>
            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Stripe invoices and manual payment records
            </p>
          </div>
        </div>

        <div
          className={`rounded-xl border p-4 mb-6 ${
            darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
          }`}
        >
          <p className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Filters
          </p>
          {clientsLoading ? (
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading clients…</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
              <div>
                <label
                  className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                >
                  Client
                </label>
                <select
                  value={clientId}
                  onChange={handleClientChange}
                  className={`w-full rounded-lg border px-3 py-2 text-sm ${
                    darkMode
                      ? 'bg-gray-900 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="">All clients</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {clientLabelFromAccount(c)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                >
                  From
                </label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className={`w-full rounded-lg border px-3 py-2 text-sm ${
                    darkMode
                      ? 'bg-gray-900 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
              <div>
                <label
                  className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                >
                  To
                </label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className={`w-full rounded-lg border px-3 py-2 text-sm ${
                    darkMode
                      ? 'bg-gray-900 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
              <div>
                <label
                  className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                >
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className={`w-full rounded-lg border px-3 py-2 text-sm ${
                    darkMode
                      ? 'bg-gray-900 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="all">All statuses</option>
                  <option value="Paid">Paid</option>
                  <option value="Failed">Failed</option>
                  <option value="Refunded">Refunded</option>
                  <option value="Processing">Processing</option>
                </select>
              </div>
              <div className="lg:col-span-1">
                <button
                  type="button"
                  onClick={() => {
                    setDateFrom('');
                    setDateTo('');
                    setStatusFilter('all');
                  }}
                  className={`w-full rounded-lg border px-3 py-2 text-sm font-medium ${
                    darkMode
                      ? 'border-gray-600 text-gray-200 hover:bg-gray-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Clear filters
                </button>
              </div>
            </div>
          )}
        </div>

        {!clientId && !clientsLoading && !clients.length && (
          <div
            className={`rounded-xl p-10 text-center ${
              darkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-600 shadow-sm'
            }`}
          >
            <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="text-lg font-medium">No client accounts found</p>
          </div>
        )}

        {!clientId && !clientsLoading && clients.length > 0 && allPaymentsLoading && (
          <div className={`text-center py-16 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Loading all transactions…
          </div>
        )}

        {!clientId && !clientsLoading && clients.length > 0 && !allPaymentsLoading && aggregatedPayments && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white p-6 text-center shadow-md flex flex-col justify-center min-h-[120px]">
                <div className="text-sm font-medium opacity-90 mb-2">Total revenue</div>
                <div className="text-3xl font-bold tracking-tight">
                  ${billingMetrics.totalRevenue.toLocaleString()}
                </div>
              </div>
              <div className="rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 text-white p-6 text-center shadow-md flex flex-col justify-center min-h-[120px]">
                <div className="text-sm font-medium opacity-90 mb-2">MRR</div>
                <div className="text-3xl font-bold tracking-tight">
                  ${billingMetrics.mrr.toLocaleString()}
                </div>
                <div className="text-xs opacity-80 mt-1">Monthly recurring revenue</div>
              </div>
              <div className="rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 text-white p-6 text-center shadow-md flex flex-col justify-center min-h-[120px]">
                <div className="text-sm font-medium opacity-90 mb-2">Active subscriptions</div>
                <div className="text-3xl font-bold tracking-tight">{billingMetrics.activeSubscriptions}</div>
              </div>
            </div>

            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Transactions & invoices
            </h3>

            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Invoice
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {paginatedTransactions.map((payment, index) => {
                    const displayStatus = paymentDisplayStatus(payment.status);
                    const receiptUrl = payment.hostedInvoiceUrl || payment.receiptUrl;
                    const invoiceRef = payment.stripeInvoiceId || payment.id || '—';
                    const rowKey = `${payment._clientId}-${payment.id || ''}-${payment.stripeInvoiceId || index}`;
                    return (
                      <tr key={rowKey} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-4 py-3 text-sm">
                          <Link
                            to={`/crm/client/${payment._clientId}`}
                            className="text-red-600 hover:underline dark:text-red-400 font-medium"
                          >
                            {payment._clientName}
                          </Link>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {payment.dueDate || payment.createdAt
                            ? new Date(
                                payment.dueDate || payment.createdAt?.toDate?.() || payment.createdAt
                              ).toLocaleDateString()
                            : 'Not set'}
                        </td>
                        <td
                          className="px-4 py-3 text-sm text-gray-900 dark:text-white font-mono max-w-[200px] truncate"
                          title={invoiceRef}
                        >
                          {receiptUrl && payment.stripeInvoiceId ? (
                            <a
                              href={receiptUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-red-600 hover:underline dark:text-red-400"
                            >
                              {invoiceRef}
                            </a>
                          ) : (
                            invoiceRef
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          ${Math.abs(payment.amount || 0).toLocaleString()}
                          {payment.amount < 0 ? ' (refund)' : ''}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${
                              displayStatus === 'Paid'
                                ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300'
                                : displayStatus === 'Failed'
                                  ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300'
                                  : displayStatus === 'Refunded'
                                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                                    : 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-300'
                            }`}
                          >
                            {displayStatus}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            {receiptUrl ? (
                              <a
                                href={receiptUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded"
                                title="View receipt or invoice"
                              >
                                <FileText className="w-4 h-4" />
                              </a>
                            ) : (
                              <span
                                className="p-1.5 text-gray-400 cursor-not-allowed rounded inline-flex"
                                title="No receipt available"
                              >
                                <FileText className="w-4 h-4" />
                              </span>
                            )}
                            <Link
                              to={`/crm/clientbilling?clientId=${encodeURIComponent(payment._clientId)}`}
                              className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded text-xs"
                              title="View this client’s billing"
                            >
                              Filter
                            </Link>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredPayments.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                        {aggregatedPayments.length === 0
                          ? 'No payment records found'
                          : 'No transactions match your filters'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {transactionsPaginationBar}
          </>
        )}

        {clientId && accountLoading && (
          <div className={`text-center py-16 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Loading billing…
          </div>
        )}

        {clientId && !accountLoading && account && (
          <>
            <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
              <div>
                <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {account.company ||
                    [account.contact?.firstName, account.contact?.lastName].filter(Boolean).join(' ') ||
                    'Client'}
                </h2>
                <Link
                  to={`/crm/client/${clientId}`}
                  className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 mt-1 inline-block"
                >
                  Open client profile →
                </Link>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleSyncStripeBilling}
                  disabled={syncBillingLoading}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${syncBillingLoading ? 'animate-spin' : ''}`} />
                  {syncBillingLoading ? 'Syncing…' : 'Sync billing / lifetime'}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div
                className={`rounded-lg p-4 text-center ${
                  darkMode ? 'bg-gray-800' : 'bg-gray-50'
                }`}
              >
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Billing cycle</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                  {(account.program?.billingCycle || account.billing?.billingCycle || 'monthly')
                    .charAt(0)
                    .toUpperCase() +
                    (account.program?.billingCycle || account.billing?.billingCycle || 'monthly').slice(1)}
                </div>
              </div>
              <div
                className={`rounded-lg p-4 text-center ${
                  darkMode ? 'bg-gray-800' : 'bg-gray-50'
                }`}
              >
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Monthly amount</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                  $
                  {(account.program?.monthlyAmount ?? account.billing?.monthlyAmount ?? 0).toLocaleString()}
                </div>
              </div>
              <div
                className={`rounded-lg p-4 text-center ${
                  darkMode ? 'bg-gray-800' : 'bg-gray-50'
                }`}
              >
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Lifetime paid</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                  ${lifetimePaidDisplayAmount.toLocaleString()}
                </div>
              </div>
            </div>

            <div
              className={`rounded-lg p-4 mb-4 ${
                darkMode ? 'bg-gray-800/80 border border-gray-700' : 'bg-white border border-gray-200 shadow-sm'
              }`}
            >
              <div className="flex flex-wrap gap-6 text-sm">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Subscription status</span>
                  <div className="font-medium text-gray-900 dark:text-white mt-0.5">
                    {(account.billing?.status || account.program?.subscriptionStatus || '—')
                      .toString()
                      .replace(/_/g, ' ')}
                  </div>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Current period</span>
                  <div className="font-medium text-gray-900 dark:text-white mt-0.5">
                    {account.billing?.currentPeriodStart
                      ? `${new Date(account.billing.currentPeriodStart).toLocaleDateString()} → ${
                          account.billing?.currentPeriodEnd
                            ? new Date(account.billing.currentPeriodEnd).toLocaleDateString()
                            : '—'
                        }`
                      : '—'}
                  </div>
                </div>
                {stripeUsage != null && (
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Strategy calls (period)</span>
                    <div className="font-medium text-gray-900 dark:text-white mt-0.5">
                      {stripeUsage?.callsUsed ?? '—'} /{' '}
                      {stripeUsage?.callsLimit ?? account.billing?.entitlements?.callsLimitPerPeriod ?? '—'}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white p-6 text-center shadow-md flex flex-col justify-center min-h-[120px]">
                <div className="text-sm font-medium opacity-90 mb-2">Total revenue</div>
                <div className="text-3xl font-bold tracking-tight">
                  ${billingMetrics.totalRevenue.toLocaleString()}
                </div>
              </div>
              <div className="rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 text-white p-6 text-center shadow-md flex flex-col justify-center min-h-[120px]">
                <div className="text-sm font-medium opacity-90 mb-2">MRR</div>
                <div className="text-3xl font-bold tracking-tight">
                  ${billingMetrics.mrr.toLocaleString()}
                </div>
                <div className="text-xs opacity-80 mt-1">Monthly recurring revenue</div>
              </div>
              <div className="rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 text-white p-6 text-center shadow-md flex flex-col justify-center min-h-[120px]">
                <div className="text-sm font-medium opacity-90 mb-2">Active subscriptions</div>
                <div className="text-3xl font-bold tracking-tight">{billingMetrics.activeSubscriptions}</div>
              </div>
            </div>

            {isDevelopment && (account.stripeCustomerId || account.billing?.stripeSubscriptionId) && (
              <div className="mb-4 p-3 rounded-lg bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800 text-xs font-mono text-purple-900 dark:text-purple-100">
                <div className="font-sans font-medium text-purple-800 dark:text-purple-200 mb-1">Billing debug</div>
                <div>stripeCustomerId: {account.stripeCustomerId || '—'}</div>
                <div>stripeSubscriptionId: {account.billing?.stripeSubscriptionId || '—'}</div>
              </div>
            )}

            <div className="relative flex items-center justify-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white text-center">
                Transactions & invoices
              </h3>
              <div className="absolute right-0">
                <AddButton onClick={() => setShowAddPaymentModal(true)} />
              </div>
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Invoice
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {paginatedTransactions.map((payment, index) => {
                    const displayStatus = paymentDisplayStatus(payment.status);
                    const receiptUrl = payment.hostedInvoiceUrl || payment.receiptUrl;
                    const invoiceRef =
                      payment.stripeInvoiceId || payment.id || '—';
                    return (
                      <tr key={payment.id || index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {payment.dueDate || payment.createdAt
                            ? new Date(
                                payment.dueDate || payment.createdAt?.toDate?.() || payment.createdAt
                              ).toLocaleDateString()
                            : 'Not set'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white font-mono max-w-[200px] truncate" title={invoiceRef}>
                          {receiptUrl && payment.stripeInvoiceId ? (
                            <a
                              href={receiptUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-red-600 hover:underline dark:text-red-400"
                            >
                              {invoiceRef}
                            </a>
                          ) : (
                            invoiceRef
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          ${Math.abs(payment.amount || 0).toLocaleString()}
                          {payment.amount < 0 ? ' (refund)' : ''}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${
                              displayStatus === 'Paid'
                                ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300'
                                : displayStatus === 'Failed'
                                  ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300'
                                  : displayStatus === 'Refunded'
                                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                                    : 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-300'
                            }`}
                          >
                            {displayStatus}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            {receiptUrl ? (
                              <a
                                href={receiptUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded"
                                title="View receipt or invoice"
                              >
                                <FileText className="w-4 h-4" />
                              </a>
                            ) : (
                              <span
                                className="p-1.5 text-gray-400 cursor-not-allowed rounded inline-flex"
                                title="No receipt available"
                              >
                                <FileText className="w-4 h-4" />
                              </span>
                            )}
                            {displayStatus === 'Failed' && (
                              <span
                                className="p-1.5 text-gray-400 cursor-not-allowed rounded inline-flex"
                                title="Retry in Stripe"
                              >
                                <RefreshCw className="w-4 h-4" />
                              </span>
                            )}
                            {!payment.stripeInvoiceId && (
                              <button
                                type="button"
                                onClick={() => handleEditPayment(payment)}
                                className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded"
                                title="Edit manual payment"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                            )}
                            <span
                              className="p-1.5 text-gray-400 cursor-not-allowed rounded inline-flex"
                              title="Payment method managed in Stripe"
                            >
                              <CreditCard className="w-4 h-4" />
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredPayments.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                        {dedupedPayments.length === 0
                          ? 'No payment records found'
                          : 'No transactions match your filters'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {transactionsPaginationBar}
          </>
        )}

        {clientId && !accountLoading && !account && (
          <div className={`rounded-2xl p-10 text-center bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm`}>
            <p className="text-gray-600 dark:text-gray-300">Could not load this client.</p>
          </div>
        )}
      </div>

      {showAddPaymentModal && clientId && account && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-lg w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <div className="flex-1" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white text-center flex-1">
                {editingPaymentId ? 'Edit payment' : 'Add payment'}
              </h3>
              <div className="flex-1 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddPaymentModal(false);
                    setEditingPaymentId(null);
                    setNewPayment({ amount: '', dueDate: '', status: 'processing' });
                  }}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Amount</label>
                <input
                  type="number"
                  value={newPayment.amount === '' || newPayment.amount === undefined ? '' : newPayment.amount}
                  onChange={(e) => {
                    const v = e.target.value;
                    setNewPayment({
                      ...newPayment,
                      amount: v === '' ? '' : parseFloat(v) || 0,
                    });
                  }}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter amount"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date</label>
                <input
                  type="date"
                  value={newPayment.dueDate}
                  onChange={(e) => setNewPayment({ ...newPayment, dueDate: e.target.value })}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                <select
                  value={
                    newPayment.status === 'overdue'
                      ? 'failed'
                      : newPayment.status === 'pending'
                        ? 'processing'
                        : newPayment.status
                  }
                  onChange={(e) =>
                    setNewPayment({
                      ...newPayment,
                      status:
                        e.target.value === 'failed'
                          ? 'failed'
                          : e.target.value === 'processing'
                            ? 'processing'
                            : 'paid',
                    })
                  }
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="paid">Paid</option>
                  <option value="failed">Failed</option>
                  <option value="processing">Processing</option>
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-between items-center">
              {editingPaymentId && (
                <button
                  type="button"
                  onClick={async () => {
                    await handleDeletePayment(editingPaymentId);
                    setShowAddPaymentModal(false);
                    setEditingPaymentId(null);
                    setNewPayment({ amount: '', dueDate: '', status: 'processing' });
                  }}
                  className="px-4 py-2 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
                >
                  Delete
                </button>
              )}
              <div className="flex space-x-3 ml-auto">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddPaymentModal(false);
                    setEditingPaymentId(null);
                    setNewPayment({ amount: '', dueDate: '', status: 'processing' });
                  }}
                  className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (newPayment.amount === '' || newPayment.amount === undefined || Number.isNaN(newPayment.amount))
                      return;
                    try {
                      if (editingPaymentId) {
                        await handleUpdatePayment(editingPaymentId, newPayment);
                      } else {
                        const payment = {
                          ...newPayment,
                          id: Date.now().toString(),
                          date: new Date().toISOString(),
                          type: 'subscription',
                          description: 'Payment',
                        };
                        const updatedPayments = [...(account.billing?.payments || []), payment];
                        const updatedAccount = {
                          ...account,
                          billing: {
                            ...account.billing,
                            payments: updatedPayments,
                          },
                        };
                        setAccount(updatedAccount);
                        await updateAccount(clientId, { 'billing.payments': updatedPayments });
                      }
                      setNewPayment({ amount: '', dueDate: '', status: 'processing' });
                      setEditingPaymentId(null);
                      setShowAddPaymentModal(false);
                    } catch (error) {
                      console.error('Error saving payment:', error);
                      alert('Failed to save payment. Please try again.');
                    }
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  {editingPaymentId ? 'Update' : 'Add payment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientBilling;
